import React, { forwardRef, ReactNode, useImperativeHandle, useRef, useState } from 'react';
import { TextInput, TextInputProps, TouchableWithoutFeedback, View } from 'react-native';
import { useTheme } from '@pulsar/core';
import { Spinner } from 'src/components/Spinner';
import { Tappable } from 'src/components/Tappable/Tappable';
import MagnifierIcon from 'src/assets/icons/search.svg';
import CrossCircleIcon from 'src/assets/icons/x-circle.svg';
import useStyles from './SearchBar.styles';

export type SearchBarProps = TextInputProps & {
  showIcon?: boolean;
  isLoading?: boolean;
  after?: ReactNode;
};

export const SearchBar = forwardRef<TextInput, SearchBarProps>(
  (
    {
      showIcon = true,
      isLoading = false,
      after,
      clearButtonMode = 'while-editing',
      style,
      ...props
    },
    ref
  ) => {
    const innerRef = useRef<TextInput>(null);
    useImperativeHandle(ref, () => innerRef.current!);
    const s = useStyles();
    const { colors } = useTheme();
    const [showClearButton, toggleClearButton] = useState(
      props.value && innerRef.current?.isFocused()
    );

    const innerOnChange: TextInputProps['onChangeText'] = text => {
      toggleClearButton(text.length > 0);
      props.onChangeText?.(text);
    };

    const innerOnBlur: TextInputProps['onBlur'] = event => {
      toggleClearButton(false);
      props.onBlur?.(event);
    };

    const innerOnFocus: TextInputProps['onFocus'] = event => {
      toggleClearButton(event.nativeEvent.text.length > 0);
      props.onFocus?.(event);
    };

    const clear = () => {
      innerRef.current?.clear();
      innerOnChange('');
    };

    return (
      <TouchableWithoutFeedback onPress={() => innerRef.current?.focus()}>
        <View style={[s.root, !after && !showClearButton && s.rootPaddingRight]}>
          {showIcon && <MagnifierIcon width={16} height={16} viewBox="0 0 24 24" style={s.icon} />}
          <TextInput
            {...props}
            ref={innerRef}
            placeholderTextColor={colors.searchBarPlaceholder}
            style={[s.input, style]}
            clearButtonMode="never"
            onChangeText={innerOnChange}
            onFocus={innerOnFocus}
            onBlur={innerOnBlur}
          />
          {isLoading && <Spinner size={16} color={colors.searchBarIcon} />}
          {after}
          {showClearButton && (
            <Tappable style={s.button} onPressIn={clear}>
              <CrossCircleIcon
                width={16}
                height={16}
                viewBox="0 0 24 24"
                color={colors.searchBarIcon}
              />
            </Tappable>
          )}
        </View>
      </TouchableWithoutFeedback>
    );
  }
);
